import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { gql } from '@apollo/client';
import { appServiceLines, commonEnums } from '@codametrix/ui-common';
import { getServiceDeskClient } from '../clients/service-desk.client';

// The BFF stores the BSP / "Bedside Pro" service line under the canonical key 'IPC'
// (see service-desk-directory-service migration WORKQ-687-2). The picker emits user-facing
// names. Collapse to the backend's canonical token before issuing the GraphQL filter.
const toBackendServiceLine = (service: string): string =>
  appServiceLines.isIPC(service) ? commonEnums.ServiceLines.IPC : service;

export interface QueueItem {
  id: string;
  queueName: string;
  status: 'healthy' | 'at-risk' | 'critical' | 'needs-attention' | 'superstar';
  // CDMX-15861
  // totalInQueue: number;
  // criticalAged: number;
  // avgBurnDownRate: number;
  coderCount: number;
}

const GET_PRIORITY_PROFILES = gql`
  query GetPriorityProfiles($pageNumber: Int, $pageSize: Int, $service: String) {
    GetPriorityProfiles(pageNumber: $pageNumber, pageSize: $pageSize, service: $service) {
      data {
        collection_id
        name
        availability
        coder_assigned
      }
      total
    }
  }
`;

const UPDATE_COLLECTION_CONFIG = gql`
  mutation UpdateCollectionConfig(
    $config: JSON
    $service: String
    $priorityOrder: [String]
  ) {
    UpdateCollectionConfig(
      config: $config
      service: $service
      priorityOrder: $priorityOrder
    )
  }
`;

const fetchQueues = createAsyncThunk<QueueItem[], { service: string }>(
  'queuePriorityOrder/fetchQueues',
  async ({ service }) => {
    const client = getServiceDeskClient();
    await client.refreshToken();
    const { data } = await client.apolloClient.query({
      query: GET_PRIORITY_PROFILES,
      variables: {
        service: toBackendServiceLine(service),
        pageNumber: 1,
        pageSize: 500
      },
      fetchPolicy: 'network-only'
    });
    const items: any[] = data?.GetPriorityProfiles?.data ?? [];
    return items.map(p => ({
      id: p.collection_id,
      queueName: p.name,
      coderCount: p.coder_assigned ?? 0,
      // TODO(CDMX-12009): replace with real status when backend status calc lands
      status: 'healthy' as const
    }));
  }
);

const savePriorityOrder = createAsyncThunk<
  string[],
  { service: string; orderedIds: string[] }
>('queuePriorityOrder/savePriorityOrder', async ({ service, orderedIds }) => {
  const client = getServiceDeskClient();
  await client.refreshToken();
  await client.apolloClient.mutate({
    mutation: UPDATE_COLLECTION_CONFIG,
    variables: {
      service: toBackendServiceLine(service),
      priorityOrder: orderedIds,
      config: {}
    }
  });
  return orderedIds;
});

const reorderQueues = createAction<{ fromIndex: number; toIndex: number }>(
  'queuePriorityOrder/reorderQueues'
);

const revertOrder = createAction('queuePriorityOrder/revertOrder');

export { fetchQueues, savePriorityOrder, reorderQueues, revertOrder };
